import type { Vector3 } from 'three';

/**
 * Constants for scene bounds calculations
 */
export const HIGHLIGHT_CIRCLE_PADDING = 36; // 36" padding for highlight light path
export const HIGHLIGHT_CIRCLE_Y_OFFSET = 60; // 60" above the tallest point

/**
 * Default camera Y position (6' = 72")
 */
export const DEFAULT_CAMERA_Y = 72;

/**
 * Default camera distance from target in the x,z plane (30' = 360")
 */
export const DEFAULT_CAMERA_XZ_DISTANCE = 360;

/**
 * Auto-zoom viewport padding factor (0% padding)
 */
export const AUTO_ZOOM_PADDING = 0.95;

/**
 * Minimum camera distance from target (prevents clipping into scene)
 */
export const CAMERA_MIN_DISTANCE = 24;

/**
 * Y offset above kitchen top (boundsMax.y) for the arc source point.
 * The auto-zoom arc starts at this height and sweeps down to the target.
 */
export const ARC_SOURCE_Y_OFFSET = 36;

/**
 * Metrics calculated from scene bounds
 */
export interface SceneBoundsMetrics {
	/** Center X coordinate of the scene */
	centerX: number;
	/** Center Z coordinate of the scene */
	centerZ: number;
	/** Width of the scene (X axis) */
	sceneWidth: number;
	/** Depth of the scene (Z axis) */
	sceneDepth: number;
	/** Diagonal distance across the scene (XZ plane) */
	sceneDiagonal: number;
	/** Radius of the kitchen circle (half the diagonal) */
	kitchenCircleRadius: number;
	/** Y position of the kitchen circle (mid-height) */
	kitchenCircleY: number;
	/** Radius of the highlight circle (kitchen radius + padding) */
	highlightCircleRadius: number;
	/** Y position of the highlight circle (above max Y) */
	highlightCircleY: number;
}

/**
 * Parameters for camera position calculation
 */
export interface CameraPositionParams {
	centerX: number;
	centerZ: number;
	circleRadius: number;
	cameraY?: number;
	distanceMultiplier?: number;
	viewingAngle?: number;
	/** Explicit x,z plane distance from target (overrides circleRadius-based calculation) */
	xzDistance?: number;
}

/**
 * Camera position and target result
 */
export interface CameraGeometryResult {
	position: Vector3;
	target: Vector3;
}

/**
 * Bounds as separate min/max Vector3 objects
 */
export interface BoundsVectors {
	min: Vector3;
	max: Vector3;
}

/**
 * Raw bounds data (before conversion to Vector3)
 */
export interface RawBounds {
	min: { x: number; y: number; z: number };
	max: { x: number; y: number; z: number };
}
